import { Analytics } from '@trello/atlassian-analytics';
import { markdown } from '@trello/markdown';

import { newCommentState } from 'app/src/components/Comments';

const updateComment = (value: string) =>
  newCommentState.setValue(({ defaultValue }) => ({
    defaultValue,
    imperativeValue: `${defaultValue} ${value} `.trimStart(),
    isFocused: true,
  }));

const focusComment = () => newCommentState.setValue({ isFocused: true });

const trackMemberMentionedEvent = (mentionCount: number) => {
  Analytics.sendTrackEvent({
    action: 'mentioned',
    actionSubject: 'member',
    attributes: {
      mentionCount,
      method: 'reply',
    },
    source: 'cardDetailScreen',
  });
};

const getMentions = (text: string): string[] =>
  // @ts-expect-error
  markdown.comments.getMatches(text, { atMention: true }).atMention;

export const replyToComment = ({
  commentText,
  commenterUsername,
  currentMemberUsername,
}: {
  commentText: string;
  commenterUsername?: string;
  currentMemberUsername?: string;
}) => {
  const { defaultValue } = newCommentState.value;
  const usersToMention: string[] = [];

  // We don't want to mention card, board, the current user, or anyone already tagged in the editor
  const disallowedMentions = [
    '@card',
    '@board',
    `@${currentMemberUsername}`,
    ...getMentions(defaultValue),
  ];

  // Mention the commenter, unless their name was already in the editor.
  // Also push them to the array of disallowedMentions to prevent duplicate mentions.
  if (
    commenterUsername &&
    !disallowedMentions.includes(`@${commenterUsername}`)
  ) {
    usersToMention.push(`@${commenterUsername}`);
    disallowedMentions.push(`@${commenterUsername}`);
  }

  // For each valid additional mention in the commentText, append it to the array.
  getMentions(commentText).forEach((mention) => {
    if (!disallowedMentions.includes(mention)) {
      usersToMention.push(mention);
    }
  });

  if (usersToMention.length) {
    updateComment(usersToMention.join(' '));
    trackMemberMentionedEvent(usersToMention.length);
  } else {
    // If there's no one new to mention, just refocus the editor.
    focusComment();
  }
};

// This is used for replying via query params. We ignore the active editor state and just place the username there.
export const replyToUser = (username: string) => {
  newCommentState.setValue({
    imperativeValue: `@${username} `,
    isFocused: true,
  });
  trackMemberMentionedEvent(1);
};

export const replyToAttachment = ({
  attachmentUrl,
  attachmentText,
  username,
}: {
  attachmentUrl: string;
  attachmentText: string;
  username?: string;
}) => {
  const { defaultValue } = newCommentState.value;
  const editorText =
    !username || defaultValue.includes(username)
      ? `[${attachmentText}](${attachmentUrl})`
      : `@${username} [${attachmentText}](${attachmentUrl})`;

  // Chopping off the last character is necessary for comparison since the markdown slightly changes once inside the editor.
  // e.g. `[my link](link.com)` becomes `[my link](link.com "")`. So instead, we just search for `[my link](link.com`.
  if (defaultValue.includes(editorText.slice(0, -1))) {
    focusComment();
  } else {
    updateComment(editorText);
    if (username) {
      trackMemberMentionedEvent(1);
    }
  }
};
